import { Edge, Position, Direction } from '../core/GridGeometry.js';
import type { FinePoint } from './KeyboardController.js';

/** The part of Grid this needs — just enough to resolve an edge. */
interface EdgeSource {
    readonly size: number;
    getEdgeFromPosition(pos: Position, dir: Direction): Edge;
}

/** The cell a half-grid point sits at the centre of, or null anywhere else. */
export function fineToCell(p: FinePoint): Position | null {
    return p.r % 2 === 1 && p.c % 2 === 1
        ? { row: (p.r - 1) / 2, col: (p.c - 1) / 2 }
        : null;
}

/**
 * The grid edge a half-grid cursor point sits on, or null when it's on a cell
 * center or an intersection (both coordinates the same parity).
 *
 * Reaches the **rim**: a point on the outer boundary resolves to the outward edge of
 * the last cell rather than running off the grid. Games that keep the cursor inside
 * (Walls) never hit that case; the ones that opt into `allowsFineRim` (Orbits' exit
 * gates, Flood's faucet and boundary walls) rely on it.
 */
export function fineToEdge(grid: EdgeSource, p: FinePoint): Edge | null {
    const rOdd = p.r % 2 === 1;
    const cOdd = p.c % 2 === 1;
    if (rOdd === cOdd) return null;

    const n = grid.size;
    if (rOdd) {
        // Vertical edge: the west side of the cell to its right, or the east rim.
        const row = (p.r - 1) / 2;
        const col = p.c / 2;
        return col === n
            ? grid.getEdgeFromPosition({ row, col: n - 1 }, 'E')
            : grid.getEdgeFromPosition({ row, col }, 'W');
    }
    // Horizontal edge: the north side of the cell below, or the south rim.
    const row = p.r / 2;
    const col = (p.c - 1) / 2;
    return row === n
        ? grid.getEdgeFromPosition({ row: n - 1, col }, 'S')
        : grid.getEdgeFromPosition({ row, col }, 'N');
}
